import useSWR, { MutatorCallback } from "swr";
import WatsonApi from "../../backends/WatsonApi";
import { createRef, Ref, useState } from "react";
import FontAwesome from "../../components/utilities/FontAwesome";
import styles from "./styles/DashboardPage.module.scss";
import moment from "moment";
import classNames from "classnames";
import { handleContentItemClick } from "../../components/blocks/SchoolBlocks/ContentItemBlock/ContentItemBlock";
import { PaginationFooter } from "../SearchPage/PaginationFooter";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import {
  IReactiveEventContentItem,
  IReactiveMediaContentItem,
  IReactiveNewsContentItem,
  ReactiveContentItem,
} from "../../components/schoolfeed/SchoolFeedContentItem";
import ClickableLink from "../../components/utilities/ClickableLink";
import { useStores } from "../../stores/useStores";
import { useFeatureFlags } from "../../utils/featureFlags/useFeatureFlags";

type CombinedShareRequest = (
  | ContentItemShareRequest
  | ContentSourceShareRequest
) & {
  type: "item" | "source";
  content_object: {
    id: string | number;
    type: "item" | "source";
    account_title: string;
    organization_id: string;
    description?: string;
  };
};

const containerClassName = classNames({
  [styles.widget]: true,
  [styles.itemApprovalsContainer]: true,
});

const transitionClassNames = {
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
};

function ApprovalItem(props: {
  item: CombinedShareRequest;
  boundMutate: MutatorCallback;
  ref: Ref<HTMLTableRowElement>;
}) {
  const { userStore } = useStores();

  const [showMessage, setShowMessage] = useState(false);
  const [reviewMessage, setReviewMessage] = useState("");

  async function handleRespond(e, approved: boolean) {
    e.stopPropagation();
    const client = await WatsonApi();

    if (props.item.type === "item") {
      await client.apis.organizations.organizations_content_item_share_requests_partial_update(
        {
          organization_pk: props.item.target_organization.id,
          id: props.item.id,
          data: {
            reviewer: userStore.id,
            approved,
            review_message: reviewMessage,
          },
        },
      );
    } else {
      await client.apis.organizations.organizations_content_source_share_requests_partial_update(
        {
          organization_pk: props.item.target_organization.id,
          id: props.item.id,
          data: {
            reviewer: userStore.id,
            approved,
            review_message: reviewMessage,
          },
        },
      );
    }

    await props.boundMutate((currentData) => ({
      ...currentData,
      requests: currentData.requests.filter(
        (request) => request.id !== props.item.id,
      ),
    }));
  }

  async function handleApproveAll(e) {
    e.stopPropagation();
    const client = await WatsonApi();

    await client.apis.organizations.organizations_content_auto_approval_create({
      organization_pk: props.item.target_organization.id,
      data: {
        created_by: userStore.id,
        source_organization: props.item.content_object.organization_id,
        target_organization: props.item.target_organization.id,
      },
    });
    props.boundMutate();
  }

  const isContentItem = props.item.type === "item";
  const title = props.item.content_object.account_title;
  const description = isContentItem
    ? props.item.content_object.description
    : null;

  return (
    <>
      <tr
        ref={props.ref}
        onClick={
          isContentItem
            ? () =>
                handleContentItemClick(
                  (props.item as ContentItemShareRequest).content_item as
                    | IReactiveEventContentItem
                    | IReactiveNewsContentItem
                    | IReactiveMediaContentItem,
                )
            : undefined
        }
      >
        <td title={title}>
          <h4>{title}</h4>
          {description && <p>{description}</p>}
        </td>
        <td title={props.item.target_organization.title}>
          <ClickableLink
            aria-label={"Link to organization page."}
            onClick={(e) => e.stopPropagation()}
            href={`/pages/${props.item.target_organization.id}`}
          >
            {props.item.target_organization.title}
          </ClickableLink>
        </td>
        <td
          title={`${props.item.requester.fname} ${props.item.requester.lname}`}
        >
          <div>
            {`${props.item.requester.fname} ${props.item.requester.lname}`}
          </div>
        </td>
        <td title={moment(props.item.created_at).format("MMM D, YYYY")}>
          {moment(props.item.created_at).format("MMM D, YYYY")}
        </td>
        <td>
          <button
            aria-label={"Approve Share Request"}
            className={`${styles.tooltipVertical} ${styles.reviewButton}`}
            data-content={"Approve"}
            onClick={(e) => handleRespond(e, true)}
          >
            <FontAwesome fixedWidth prefix={"fas"} name={"fa-check"} />
          </button>
          <button
            aria-label={"Always Approve Share Requests"}
            className={`${styles.tooltipVertical} ${styles.reviewButton}`}
            data-content={"Always Approve"}
            onClick={handleApproveAll}
          >
            <FontAwesome fixedWidth prefix={"fas"} name={"fa-check-double"} />
          </button>
          <button
            aria-label={"Reject Share Request"}
            className={`${styles.tooltipVertical} ${styles.reviewButton}`}
            data-content={"Reject"}
            onClick={(e) => {
              e.stopPropagation();
              setShowMessage((v) => !v);
            }}
          >
            <FontAwesome fixedWidth prefix={"fas"} name={"fa-times"} />
          </button>
        </td>
      </tr>
      {showMessage && (
        <tr className={styles.requestConfirm}>
          <td colSpan={5}>
            <textarea
              onChange={(e) => setReviewMessage(e.target.value)}
              placeholder={"Reason for rejection (optional)"}
            >
              {reviewMessage}
            </textarea>
            <button onClick={(e) => handleRespond(e, false)}>Submit</button>
          </td>
        </tr>
      )}
    </>
  );
}

export function ContentItemApprovalWidget() {
  const { userStore, modalStore } = useStores();
  const { content_source_sharing_enabled } = useFeatureFlags();

  const [page, setPage] = useState(1);
  const [requestType, setRequestType] = useState<"item" | "source">("item");

  const { data, isLoading, mutate } = useSWR(
    `my-approvals?page=${page}&type=${requestType}`,
    async () => {
      const client = await WatsonApi();

      if (requestType === "item") {
        const response =
          await client.apis.users.users_content_item_share_requests_list({
            user_pk: userStore.id,
            expand: [
              "content_item",
              "requester",
              "target_organization",
              "content_item.organization",
            ],
            approved__isnull: true,
            page: page,
            page_size: 10,
          });
        const result = JSON.parse(response.data);
        return {
          requests: result.data.map((item) => ({
            ...item,
            type: "item" as const,
            content_item: item.content_item
              ? new ReactiveContentItem(item.content_item)
              : undefined,
            content_object: {
              id: item.content_item?.id,
              type: "item" as const,
              account_title: item.content_item?.title || "Unknown",
              organization_id: item.content_item?.organization_id,
              description: item.content_item?.description,
            },
          })),
          total: Math.ceil(result.meta.total / 10),
        };
      } else {
        const response =
          await client.apis.users.users_content_source_share_requests_list({
            user_pk: userStore.id,
            expand: ["content_source", "requester", "target_organization"],
            approved__isnull: true,
            page: page,
            page_size: 10,
          });
        const result = JSON.parse(response.data);
        return {
          requests: result.data.map((item) => ({
            ...item,
            type: "source" as const,
            content_object: {
              id: item.content_source?.id,
              type: "source" as const,
              account_title:
                item.content_source?.account_title ||
                item.content_source?.source_url ||
                "Unknown",
              organization_id: item.content_source?.organization,
            },
          })),
          total: Math.ceil(result.meta.total / 10),
        };
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const total = data?.requests.length;
  return (
    <div className={containerClassName}>
      <div className={styles.title}>
        <span>Content Approval Requests</span> {isLoading || <>{total}</>}
      </div>
      {isLoading ? (
        <div className={styles.loading}>
          <FontAwesome name={"fa-spinner"} spin prefix={"fas"} />
        </div>
      ) : (
        <div>
          {content_source_sharing_enabled && (
            <div className={styles.filterTabs}>
              <button
                className={requestType === "item" ? styles.active : ""}
                onClick={() => {
                  setRequestType("item");
                  setPage(1);
                }}
              >
                Items
              </button>
              <button
                className={requestType === "source" ? styles.active : ""}
                onClick={() => {
                  setRequestType("source");
                  setPage(1);
                }}
              >
                Sources
              </button>
            </div>
          )}
          <table className={styles.requestTable}>
            <colgroup>
              <col />
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Title</th>
                <th>Target Page</th>
                <th>Initiator</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data && data.requests.length > 0 ? (
                <TransitionGroup component={null}>
                  {data.requests.map((d) => {
                    const ref = createRef<HTMLTableRowElement>();
                    return (
                      <CSSTransition
                        key={d.id}
                        classNames={transitionClassNames}
                        timeout={250}
                        nodeRef={ref}
                      >
                        <ApprovalItem ref={ref} item={d} boundMutate={mutate} />
                      </CSSTransition>
                    );
                  })}
                </TransitionGroup>
              ) : (
                <tr>
                  <td colSpan={5} style={{ pointerEvents: "none" }}>
                    No Pending Requests
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {data && (
            <PaginationFooter
              currentPage={page}
              setPage={setPage}
              totalPages={data.total}
            />
          )}
          <div className={styles.footerLink}>
            <ClickableLink
              title={"AutoApprove management"}
              aria-label={"AutoApprove management"}
              className={styles.manageButton}
              onClick={() => {
                modalStore.addModal({
                  type: "autoApproveManagement",
                  id: "autoApproveManagement",
                });
              }}
            >
              View Auto-Approvals{" "}
              <div
                className={styles.tooltip}
                data-content={
                  "Auto-Approvals currently enabled for pages you administrate"
                }
              >
                <FontAwesome prefix={"far"} name={"fa-question-circle"} />
              </div>
            </ClickableLink>
          </div>
        </div>
      )}
    </div>
  );
}
